import { formatLabel } from '../utils/formatText';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Clock, Instagram } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const { settings } = useSiteSettings();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/categories`);
        if (response.data?.length > 0) {
          setCategories(response.data.slice(0, 4));
        }
      } catch (error) {
        console.log('Erro ao carregar categorias');
      }
    };
    fetchCategories();
  }, []);

  return (
    <footer className="bg-[#0A0A0A] text-gray-300" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/">
              <object
                data="/logo-disneylandia.svg"
                type="image/svg+xml"
                className="h-9 w-auto pointer-events-none mb-6"
                aria-label="Disneylândia Cosméticos"
                data-testid="footer-logo"
              >
                Disneylândia
              </object>
            </Link>
            <p className="text-sm leading-relaxed text-gray-400 mb-6 font-medium">
              Referência em cosméticos, perfumaria e presentes em Canaã dos Carajás.
            </p>
            <a
              href={`https://instagram.com/${settings.site_instagram?.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#EA1266] hover:text-[#d00f58] transition-colors font-medium"
              data-testid="footer-instagram"
            >
              <Instagram size={18} />
              {settings.site_instagram}
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-6">Navegação</h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Início' },
                { to: '/catalogo', label: 'Catálogo' },
                { to: '/marcas', label: 'Marcas' },
                { to: '/sobre', label: 'Sobre Nós' },
                { to: '/contato', label: 'Contato' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors font-medium">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/admin-painel-gestao" className="text-gray-900 hover:text-gray-800 transition-colors text-xs opacity-20">
                  .
                </Link>
              </li>
            </ul>
          </div>

          {/* Categorias */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-6">Categorias</h4>
            <ul className="space-y-3">
              {categories.length > 0 ? (
                <>
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <Link 
                        to={`/catalogo?categoria=${cat.id}`} 
                        className="text-sm text-gray-400 hover:text-white transition-colors font-medium"
                      >
                        {formatLabel(cat.name)}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link 
                      to="/catalogo" 
                      className="text-sm text-[#EA1266] hover:text-[#d00f58] transition-colors font-medium"
                    >
                      Ver mais
                    </Link>
                  </li>
                </>
              ) : (
                <li>
                  <Link to="/catalogo" className="text-sm text-gray-400 hover:text-white transition-colors font-medium">
                    Ver Catalogo
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-6">Contato</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#64BC46] mt-1 flex-shrink-0" />
                <div className="text-sm font-medium text-gray-400">
                  <p>{settings.site_address}</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#64BC46] flex-shrink-0" />
                <a href={`mailto:${settings.site_email}`} className="text-sm text-gray-400 hover:text-white transition-colors font-medium">
                  {settings.site_email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} className="text-[#64BC46] mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-400">
                  Segunda a Sábado, 08h às 18h
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800/50">
        <div className="container mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-gray-500 text-center md:text-left font-medium">
              &copy; {new Date().getFullYear()} Disneylândia Cosméticos e Presentes. Canaã dos Carajás - PA.
            </p>
            
            <a 
              href="https://instagram.com/gfcomunicacaoo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-600 hover:text-[#64BC46] transition-colors"
            >
              <span className="text-xs font-medium">Desenvolvido por</span>
              <span className="font-semibold text-xs">GF Comunicação</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
